import React, { useEffect, useState, useRef } from 'react';
import Globe from 'react-globe.gl';
import * as turf from '@turf/turf';
import BottomPopup from './BottomPopup';
import './css/VisitedGlobe.css';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { FaBars } from 'react-icons/fa';
import SettingsPage from './SettingsPage';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.REACT_APP_BACKEND_URL;

countries.registerLocale(enLocale);

function VisitedGlobe({ user }) {
    const [countryFeatures, setCountryFeatures] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [playOnTV, setPlayOnTV] = useState(true);
    const globeRef = useRef();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [settingsSection, setSettingsSection] = useState(null);
    const [userSettings, setUserSettings] = useState(null);

    const getInitialDarkMode = (timezone = 'Europe/Oslo') => {
        try {
            const now = new Date();
            const localHour = new Intl.DateTimeFormat('en-US', {
                hour: 'numeric',
                hour12: false,
                timeZone: timezone
            }).format(now);
            const hour = parseInt(localHour, 10);
            return hour < 7 || hour >= 19;
        } catch (e) {
            console.warn('Invalid timezone, falling back to local time.');
            const hour = new Date().getHours();
            return hour < 7 || hour >= 19;
        }
    };

    //const [isDarkMode, setIsDarkMode] = useState(true); //In case you want manualy change between dark and light mode
    const [isDarkMode, setIsDarkMode] = useState(null);
    const toggleDarkMode = () => setIsDarkMode(prev => !prev);

    const handleLogout = () => {
        localStorage.removeItem('token');
        setUserSettings(null);
        window.location.href = '/'; // Redirect to root without re-triggering login
    };

    useEffect(() => {
        document.body.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    useEffect(() => {
        if (user?.sub) {
            fetch(`${BACKEND_URL}/user-settings?googleId=${user.sub}`)
                .then(async res => {
                    const text = await res.text();
                    if (!res.ok) {
                        if (res.status === 404) {
                            // Create user in users table (if not already exists)
                            await fetch(`${BACKEND_URL}/users`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                    googleId: user.sub,
                                    name: user.name,
                                    email: user.email
                                })
                            });

                            // Then create user settings
                            const createRes = await fetch(`${BACKEND_URL}/user-settings`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                    googleId: user.sub,
                                    theme_mode: 'auto',
                                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                                    name: user.name,
                                    email: user.email
                                })
                            });

                            if (!createRes.ok) {
                                const createText = await createRes.text();
                                throw new Error(`Failed to create user settings: ${createRes.status} ${createText}`);
                            }

                            return createRes.json();
                        } else {
                            throw new Error(`Failed to fetch user settings: ${res.status} ${text}`);
                        }
                    }
                    return JSON.parse(text);
                })
                .then(data => {
                    setUserSettings(data);
                    if (data.theme_mode === 'dark') setIsDarkMode(true);
                    else if (data.theme_mode === 'light') setIsDarkMode(false);
                    else setIsDarkMode(getInitialDarkMode(data.timezone));
                })
                .catch(error => {
                    console.error("❌ Error loading user settings:", error);
                });
        }
    }, [user]);

    useEffect(() => {
        if (!user) return;
        loadCountryData();
    }, [user]);

    const loadCountryData = () => {
        Promise.all([
            fetch('/data/world.geojson').then(res => res.json()),
            fetch(`${BACKEND_URL}/countries?googleId=${user.sub}`).then(res => res.json())
        ])
            .then(([worldData, visitData]) => {
                const visitMap = Object.fromEntries(visitData.map(d => [d.country_code, d]));

                worldData.features.forEach(f => {
                    const code = f.id;
                    if (!code) return;

                    const visit = visitMap[code];
                    f.properties.visitCount = visit?.visit_count || 0;
                    f.properties.visitStatus = visit?.visit_status || null;
                    f.properties.instagram = visit?.instagram || null;
                    f.properties.youtube = visit?.youtube || [];
                    f.properties.link = visit?.link || null;
                    f.properties.name = f.properties.ADMIN || f.properties.name;

                    const center = turf.centroid(f);
                    f.properties.lat = center.geometry.coordinates[1];
                    f.properties.lng = center.geometry.coordinates[0];
                });

                setCountryFeatures(worldData.features);
            });
    };

    useEffect(() => {
        if (countryFeatures.length > 0 && globeRef.current) {
            globeRef.current.pointOfView({ lat: 50, lng: 10, altitude: 1.5 }, 500);
        }
    }, [countryFeatures]);

    if (isDarkMode === null || isDarkMode === undefined) return null;

    return (
        <div style={{ height: '100vh', width: '100%' }}>

            <div style={{
                position: 'absolute',
                top: 10,
                left: 10,
                right: 10,
                zIndex: 2000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 10px'
            }}>
                <div
                    style={{
                        background: isDarkMode ? '#121212' : '#ffffff',
                        color: isDarkMode ? '#ffffff' : '#000000',
                        padding: '8px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                        cursor: 'pointer',
                        fontSize: '20px'
                    }}
                    onClick={() => setIsMenuOpen(prev => !prev)}
                >
                    <FaBars />
                </div>

                <div style={{
                    background: isDarkMode ? '#121212' : '#ffffff',
                    color: isDarkMode ? '#ffffff' : '#000000',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                    fontWeight: 'bold'
                }}>
                    Visited {countryFeatures.filter(f => f.properties.visitCount > 0).length} of 193 countries
                </div>

            </div>

            <Globe
                ref={globeRef}
                globeImageUrl={isDarkMode
                    ? '//unpkg.com/three-globe/example/img/earth-night.jpg'
                    : '//unpkg.com/three-globe/example/img/earth-blue-marble.jpg'}
                backgroundColor={isDarkMode ? 'black' : '#87CEEB'}

                // COUNTRY POLYGONS
                polygonsData={countryFeatures}
                polygonCapColor={d => {
                    if (d.properties.visitStatus === 'lived') return userSettings?.color_lived || '#9FDBB6';
                    if (d.properties.visitStatus === 'want') return userSettings?.color_want || '#B6C2DB';
                    if (d.properties.visitCount > 0) return userSettings?.color_been || '#DBB6A6';
                    return 'rgba(255, 255, 255, 0.1)';
                }}
                polygonSideColor={() => 'rgba(0, 0, 0, 0.05)'}
                polygonStrokeColor={() => '#111'}
                polygonsTransitionDuration={300}
                polygonAltitude={() => 0.005}

                htmlElementsData={countryFeatures.filter(d => d.properties.visitCount > 0)}
                htmlLat={d => d.properties.lat}
                htmlLng={d => d.properties.lng}
                htmlElement={d => {
                    const iso2 = countries.alpha3ToAlpha2(d.id || '');
                    const el = document.createElement('div');
                    el.className = 'country-count-badge';
                    el.style.backgroundImage = `url("https://flagcdn.com/w40/${iso2.toLowerCase()}.png")`;

                    const textSpan = document.createElement('span');
                    if (d.properties.visitStatus === 'lived') {
                        textSpan.innerHTML = `
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="white" viewBox="0 0 448 512">
                            <path d="M438.6 105.4c12.5 12.5 12.5 32.8 
                            0 45.3l-240 240c-12.5 12.5-32.8 12.5-45.3 
                            0l-120-120c-12.5-12.5-12.5-32.8 
                            0-45.3s32.8-12.5 45.3 0L192 306.7 
                            393.4 105.4c12.5-12.5 32.8-12.5 
                            45.3 0z"/>
                          </svg>
                        `;
                        textSpan.title = "Lived here";
                    } else if (d.properties.visitStatus === 'want') {
                        textSpan.innerHTML = `
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="white" viewBox="0 0 384 512">
  <path d="M144 0c-8.8 0-16 7.2-16 16V96H96c-17.7 0-32 14.3-32 32v64H320V128c0-17.7-14.3-32-32-32H256V16c0-8.8-7.2-16-16-16H144zM224 96H160V32h64V96zM64 224v224c0 17.7 14.3 32 32 32s32-14.3 32-32H256c0 17.7 14.3 32 32 32s32-14.3 32-32V224H64z"/>
</svg>
                        `;
                        textSpan.title = "Want to visit";
                    } else {
                        textSpan.textContent = d.properties.visitCount;
                    }
                    textSpan.className = 'badge-text';
                    el.appendChild(textSpan);

                    el.onclick = () => {
                        setSelectedCountry({
                            name: d.properties.name,
                            code: d.id,
                            instagram: d.properties.instagram,
                            youtube: d.properties.youtube,
                            link: d.properties.link
                        });
                    };

                    return el;
                }}

                // CLICK HANDLER
                onPolygonClick={d => {
                    if (d.properties.visitCount > 0) {
                        setSelectedCountry({
                            name: d.properties.name,
                            code: d.id,
                            instagram: d.properties.instagram,
                            youtube: d.properties.youtube,
                            link: d.properties.link
                        });
                    }
                }}
            />


            {selectedCountry && (
                <BottomPopup
                    country={selectedCountry}
                    playOnTV={playOnTV}
                    onTogglePlayMode={() => setPlayOnTV(prev => !prev)}
                    onClose={() => setSelectedCountry(null)}
                    darkMode={isDarkMode}
                    userSettings={userSettings}
                />
            )}

            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                height: '100%',
                width: '280px',
                background: isDarkMode ? '#121212' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000',
                padding: '16px 20px',
                boxShadow: '2px 0 10px rgba(0,0,0,0.3)',
                zIndex: 3000,
                display: 'flex',
                flexDirection: 'column',
                transform: isMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
                transition: 'transform 0.3s ease-in-out',
                fontSize: '16px'
            }}>
                <button
                    onClick={() => setIsMenuOpen(false)}
                    style={{
                        alignSelf: 'flex-end',
                        fontSize: '24px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: isDarkMode ? '#ffffff' : '#000000'
                    }}
                >
                    ×
                </button>

                <ul style={{ listStyle: 'none', padding: 0, marginTop: '20px' }}>
                    <li style={{ padding: '12px 0', borderBottom: '1px solid #eee' }}>
                        <button
                          onClick={() => {
                            setIsMenuOpen(false);
                            setShowSettings(true);
                            setSettingsSection('general');
                          }}
                          style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 'inherit', cursor: 'pointer' }}
                        >
                          General
                        </button>
                    </li>
                    <li style={{ padding: '12px 0', borderBottom: '1px solid #eee' }}>
                        <button
                          onClick={() => {
                            setIsMenuOpen(false);
                            setShowSettings(true);
                            setSettingsSection('countries');
                          }}
                          style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 'inherit', cursor: 'pointer' }}
                        >
                            Countries
                        </button>
                    </li>
                    <li style={{ padding: '12px 0', borderBottom: '1px solid #eee' }}>
                        <button
                          onClick={() => {
                            setIsMenuOpen(false);
                            setShowSettings(true);
                            setSettingsSection('ha');
                          }}
                          style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 'inherit', cursor: 'pointer' }}
                        >
                          Home Assistant
                        </button>
                    </li>
                    <li style={{ padding: '12px 0' }}>
                        <button
                          onClick={() => {
                            handleLogout();
                            setIsMenuOpen(false);
                          }}
                          style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 'inherit', cursor: 'pointer' }}
                        >
                            Logout
                        </button>
                    </li>
                </ul>
            </div>

            {showSettings && (
              <SettingsPage
                  onClose={() => {
                      if (!isMenuOpen) {
                          setIsMenuOpen(true);
                      }
                      setShowSettings(false);
                  }}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                playOnTV={playOnTV}
                setPlayOnTV={setPlayOnTV}
                countryFeatures={countryFeatures}
                section={settingsSection}
                user={user}
                loadCountryData={loadCountryData}
              />
            )}

        </div>
    );
}

export default VisitedGlobe;